import React, { useEffect, useRef, useState } from "react";

export interface FilterDropdownOption {
  id: string;
  label: string;
}

interface FilterDropdownProps {
  label: string;
  options: FilterDropdownOption[];
  selected: string[];
  onChange: (value: string, isChecked: boolean) => void;
}

// A compact multi-select filter, replacing the old always-open Sidebar -
// each filter is its own dropdown button so the row wraps naturally on
// mobile instead of needing a separate collapse/toggle affordance.
const FilterDropdown: React.FC<FilterDropdownProps> = ({
  label,
  options,
  selected,
  onChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (options.length === 0) return null;

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className={`flex items-center gap-2 px-4 py-2 rounded-md border text-sm font-medium transition-colors ${
          selected.length > 0
            ? "border-blue-600 text-blue-600 bg-blue-50"
            : "border-gray-300 text-gray-700 bg-white hover:bg-gray-50"
        }`}
      >
        {label}
        {selected.length > 0 && (
          <span className="flex items-center justify-center w-5 h-5 text-xs rounded-full bg-blue-600 text-white">
            {selected.length}
          </span>
        )}
        <span className={`text-xs transition-transform ${isOpen ? "rotate-180" : ""}`}>
          ▼
        </span>
      </button>

      {isOpen && (
        <div className="absolute z-20 mt-1 w-56 max-h-64 overflow-y-auto bg-white border border-gray-200 rounded-md shadow-lg py-1">
          {options.map((option) => (
            <label
              key={option.id}
              className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer"
            >
              <input
                type="checkbox"
                className="accent-blue-600"
                checked={selected.includes(option.id)}
                onChange={(e) => onChange(option.id, e.target.checked)}
              />
              {option.label}
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

export default FilterDropdown;
